import axios, { AxiosInstance } from "axios";
import authorizeService from "../../../Identity/Utilities/AuthorizeService";
import type {
  Note,
  EntityNoteCategory,
  EntityType,
} from "../Components/Notes/EntityNotesPanel";
import { normalizeNoteCategory } from "../Components/Notes/noteCategories";

/**
 * Backend note shape as returned by `web/v1/notes/...` endpoints.
 *
 * Mirrors the design doc:
 *   { id, siteId, entityType, entityId, userId, user, comment, timestamp, categories? }
 *
 * `categories` is optional because the backend hasn't shipped it yet; once it lands
 * it should be an array of category strings matching `EntityNoteCategory`.
 */
interface NoteBackendDto {
  id: string;
  siteId?: string;
  entityType: EntityType;
  entityId: string;
  entityName?: string;
  userId: string;
  user: string;
  comment: string;
  timestamp: string;
  categories?: EntityNoteCategory[];
  /** `{ serialNo, deviceType }` for Endpoint/Device; `{}` otherwise. Always present, never null. */
  entityMetadata?: Record<string, string>;
  /** Restricted to internal (GreaseBoss) users. */
  isInternal?: boolean;
  /** Whether the note's author is a GreaseBoss (internal) user. */
  isGbUser?: boolean;
  /** Only present on the site-wide rollup (GET web/v1/notes/assets). */
  assetName?: string;
}

/** Payload sent to POST/PUT note endpoints. `userId`/`user` are resolved server-side from the JWT. */
export interface NoteWriteDto {
  comment: string;
  categories?: EntityNoteCategory[];
  /** Restrict visibility to internal (GreaseBoss) users. Enforced server-side. */
  isInternalOnly?: boolean;
}

/** Maps the UI write payload to the backend body shape (`isInternalOnly` → `isInternal`). */
function toWriteBody(payload: NoteWriteDto) {
  return {
    comment: payload.comment,
    categories: payload.categories,
    isInternal: payload.isInternalOnly,
  };
}

class NotesApiService {
  axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_BACKEND_URL,
    });
  }

  private async getAuthorizedHeaders(includeJsonContentType = false) {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
    };

    if (includeJsonContentType) {
      headers["Content-Type"] = "application/json";
    }

    return headers;
  }

  /** Maps the raw backend payload to the UI's single normalized `Note` shape. */
  private normalize(dto: NoteBackendDto): Note {
    return {
      id: dto.id,
      entityType: dto.entityType,
      entityId: dto.entityId,
      entityName: dto.entityName ?? "",
      entityMetadata: dto.entityMetadata ?? {},
      description: dto.comment ?? "",
      categories: (dto.categories ?? []).map(normalizeNoteCategory),
      userId: dto.userId ?? "",
      createdBy: dto.user ?? "",
      createdAt: dto.timestamp ? new Date(dto.timestamp) : new Date(),
      isInternalOnly: dto.isInternal ?? false,
      isGbUser: dto.isGbUser ?? false,
      ...(dto.assetName !== undefined ? { assetName: dto.assetName } : {}),
    };
  }

  /** GET web/v1/notes/{entityType}/{entityId} — full note history for an entity. */
  async getNotesForEntity(entityType: EntityType, entityId: string): Promise<Note[]> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/notes/${entityType}/${entityId}`,
      headers: await this.getAuthorizedHeaders(),
    });

    const data = response.data;
    const items: NoteBackendDto[] = Array.isArray(data) ? data : (data?.notes ?? []);
    return items.map((item) => this.normalize(item));
  }

  /** POST web/v1/notes/{entityType}/{entityId} — create a new note on the entity. */
  async createNote(
    entityType: EntityType,
    entityId: string,
    payload: NoteWriteDto,
  ): Promise<Note> {
    const response = await this.axiosInstance({
      method: "POST",
      url: `web/v1/notes/${entityType}/${entityId}`,
      headers: await this.getAuthorizedHeaders(true),
      data: toWriteBody(payload),
    });

    return this.normalize(response.data);
  }

  /** PUT web/v1/notes/{noteId} — update an existing note (own notes only, enforced server-side). */
  async updateNote(noteId: string, payload: NoteWriteDto): Promise<Note> {
    const response = await this.axiosInstance({
      method: "PUT",
      url: `web/v1/notes/${noteId}`,
      headers: await this.getAuthorizedHeaders(true),
      data: toWriteBody(payload),
    });

    return this.normalize(response.data);
  }

  /** DELETE web/v1/notes/{noteId} — delete a note (own notes only, enforced server-side). */
  async deleteNote(noteId: string): Promise<void> {
    await this.axiosInstance({
      method: "DELETE",
      url: `web/v1/notes/${noteId}`,
      headers: await this.getAuthorizedHeaders(),
    });
  }

  /**
   * GET web/v1/notes/assets/{assetId} — rollup of every note across the asset (machine) and its
   * child entities (endpoints, gateways, grease pumps), optionally filtered by a date range.
   * Both date params are ISO 8601 (UTC).
   */
  async getAllAssetNotes(
    assetId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<Note[]> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/notes/assets/${assetId}`,
      headers: await this.getAuthorizedHeaders(),
      params,
    });

    const data = response.data;
    const items: NoteBackendDto[] = Array.isArray(data) ? data : (data?.notes ?? []);
    return items.map((item) => this.normalize(item));
  }

  /**
   * GET web/v1/notes/assets — site-wide rollup of every note across all assets and their child
   * entities, filtered by a date range. Both date params are ISO 8601 (UTC) and required server-side.
   */
  async getAllSiteNotes(startDate?: string, endDate?: string): Promise<Note[]> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/notes/assets`,
      headers: await this.getAuthorizedHeaders(),
      params,
    });

    const data = response.data;
    const items: NoteBackendDto[] = Array.isArray(data) ? data : (data?.notes ?? []);
    return items.map((item) => this.normalize(item));
  }
}

const notesApiService = new NotesApiService();
export default notesApiService;
